import { useQuery } from "@tanstack/react-query";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { formatDate, formatRelativeTime, getDeviceIcon, getCountryFlag } from "../lib/utils";
import {
  Mail,
  MousePointerClick,
  TrendingUp,
  Send,
  Monitor,
  Smartphone,
  Tablet,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const COLORS = ["#2563eb", "#10b981", "#f59e0b"];

const StatCard = ({ title, value, icon: Icon, subtitle }) => (
  <Card className="bg-white border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">
            {title}
          </p>
          <p className="text-3xl font-bold font-heading tabular-nums">{value}</p>
          {subtitle && (
            <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
          )}
        </div>
        <div className="p-3 bg-primary/10 rounded-xl">
          <Icon className="h-6 w-6 text-primary" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const StatCardSkeleton = () => (
  <Card className="bg-white border border-slate-100">
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-8 w-24" />
        </div>
        <Skeleton className="h-12 w-12 rounded-xl" />
      </div>
    </CardContent>
  </Card>
);

const DashboardPage = () => {
  const { data: stats, isLoading } = useQuery({
    queryKey: ["dashboard-stats"],
    queryFn: async () => {
      const response = await api.get("/dashboard/stats");
      return response.data;
    },
  });

  const deviceData = stats?.devices
    ? [
        { name: "Desktop", value: stats.devices.desktop || 0 },
        { name: "Mobile", value: stats.devices.mobile || 0 },
        { name: "Tablet", value: stats.devices.tablet || 0 },
      ].filter((d) => d.value > 0)
    : [];

  const DeviceIcon = ({ device }) => {
    switch (device) {
      case "mobile":
        return <Smartphone className="h-4 w-4" />;
      case "tablet":
        return <Tablet className="h-4 w-4" />;
      default:
        return <Monitor className="h-4 w-4" />;
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-8" data-testid="dashboard-loading">
        <div>
          <h1 className="text-3xl font-bold font-heading">Dashboard</h1>
          <p className="text-muted-foreground">Panoramica delle tue campagne email</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <StatCardSkeleton key={i} />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-80" />
          <Skeleton className="h-80" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold font-heading">Dashboard</h1>
        <p className="text-muted-foreground">Panoramica delle tue campagne email</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Totale Email"
          value={stats?.totalRecipients || 0}
          icon={Mail}
          subtitle={`${stats?.uniqueOpeners || 0} aperture uniche`}
        />
        <StatCard
          title="Tasso Apertura"
          value={`${stats?.avgOpenRate || 0}%`}
          icon={TrendingUp}
          subtitle="Media globale"
        />
        <StatCard
          title="Click Totali"
          value={stats?.totalClicks || 0}
          icon={MousePointerClick}
          subtitle="Su tutti i link"
        />
        <StatCard
          title="Campagne Attive"
          value={stats?.totalCampaigns || 0}
          icon={Send}
          subtitle="Totale campagne"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 gap-6">
        {/* Line Chart - Opens by Day */}
        <Card className="bg-white border border-slate-100 shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Aperture ultimi 30 giorni</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stats?.opensByDay || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => value.slice(5)}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#fff",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                    }}
                    formatter={(value, name) => [value, name === "opens" ? "Aperture" : name]}
                  />
                  <Line
                    type="monotone"
                    dataKey="opens"
                    stroke="#2563eb"
                    strokeWidth={2}
                    dot={{ fill: "#2563eb", strokeWidth: 2 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Second Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Bar Chart - Opens by Hour */}
        <Card className="bg-white border border-slate-100 shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Aperture per ora del giorno</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats?.opensByHour || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="hour" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#fff",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                    }}
                    labelFormatter={(value) => `Ora: ${value}:00`}
                    formatter={(value, name) => [value, name === "opens" ? "Aperture" : name]}
                  />
                  <Bar dataKey="opens" fill="#2563eb" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Pie Chart - Devices */}
        <Card className="bg-white border border-slate-100 shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Dispositivi</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 flex items-center justify-center">
              {deviceData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={deviceData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, percent }) =>
                        `${name} ${(percent * 100).toFixed(0)}%`
                      }
                    >
                      {deviceData.map((_, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-muted-foreground">Nessun dato disponibile</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity Table */}
      <Card className="bg-white border border-slate-100 shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Attività recente</CardTitle>
        </CardHeader>
        <CardContent>
          {stats?.recentEvents?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Email
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Campagna
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Ora
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Dispositivo
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Paese
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recentEvents.map((event, index) => (
                    <tr
                      key={event.id}
                      className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors"
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-primary text-sm font-medium">
                              {event.email?.charAt(0).toUpperCase() || "?"}
                            </span>
                          </div>
                          <span className="font-medium text-sm">{event.email || "N/D"}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">
                        {event.campaignName || "N/D"}
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">
                        {formatRelativeTime(event.openedAt)}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <DeviceIcon device={event.device} />
                          <span className="capitalize">{event.device || "N/D"}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm">
                        <span className="mr-1">{getCountryFlag(event.country)}</span>
                        {event.country || "N/D"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <Mail className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-muted-foreground">Nessuna attività recente</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
